import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Play, RotateCcw, Loader } from 'lucide-react';

import {
  ToolPageHeader,
  ErrorBanner,
  SuccessBanner,
  ValidationWarningBanner,
  FileUploadArea,
  DataPreviewTable,
  ToolConfigurationPanel,
  ToolActionsPanel,
  ToolInfoPanel,
  FormField,
  ActionButton,
  InfoItem,
  JobProgressOverlay,
} from '../components/tools';
import { useExcelProcessor } from '../hooks/useExcelProcessor';
import { apiService } from '../services';
import { downloadFileFromUrl } from '../utils/download';
import type { ListParserRequest, PriceList } from '../services/types';
import { autoMapColumns, looksLikeLineNumbers } from '../utils/columnMapper';
import { Breadcrumb } from '../components/Breadcrumb';

export const ListParserPage: React.FC = () => {
  const { t } = useTranslation();
  const excel = useExcelProcessor();
  const [machineType, setMachineType] = useState<
    'SIDEL' | 'KHS' | 'KRONES' | 'N/A'
  >('SIDEL');
  const [priceList, setPriceList] = useState<string>('REG_USD');
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [priceListsLoading, setPriceListsLoading] = useState(true);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [columnMappings, setColumnMappings] = useState({
    partNumber: 1,
    description: 2,
    quantity: 3,
  });
  const [enableColorCoding, setEnableColorCoding] = useState(true);
  const [isProcessingQuote, setIsProcessingQuote] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<
    'idle' | 'submitted' | 'processing' | 'completed' | 'failed'
  >('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [showValidation, setShowValidation] = useState(true);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [forceLineNumbers, setForceLineNumbers] = useState(false);

  // Use ref to store abort controller for job polling
  const pollAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch price lists on component mount
  useEffect(() => {
    const fetchPriceLists = async () => {
      try {
        setPriceListsLoading(true);
        const lists = await apiService.getPriceLists();
        setPriceLists(lists);
        // Set default price list if available
        if (lists.length > 0 && !lists.find((l) => l.id === priceList)) {
          setPriceList(lists[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch price lists:', error);
        // Fall back to hardcoded options if API fails
        setPriceLists([
          { id: 'REG_USD', description: 'Regular USD', currency: 'USD' },
          { id: 'REG_CAD', description: 'Regular CAD', currency: 'CAD' },
          { id: 'REG_EUR', description: 'Regular EUR', currency: 'EUR' },
          { id: 'DIST_USD', description: 'Distributor USD', currency: 'USD' },
          { id: 'DIST_EUR', description: 'Distributor EUR', currency: 'EUR' },
        ]);
      } finally {
        setPriceListsLoading(false);
      }
    };
    fetchPriceLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-map columns when Excel data is loaded
  useEffect(() => {
    if (excel.previewData.length > 0) {
      const headers = excel.previewData[0].map((cell) =>
        cell ? String(cell) : null
      );
      const mappings = autoMapColumns(headers);
      setColumnMappings(mappings);
    }
  }, [excel.previewData]);

  // Trigger validation when full data or column mappings change
  useEffect(() => {
    if (excel.fullData.length > 0) {
      excel.validateData(columnMappings);
      setShowValidation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excel.fullData, columnMappings, excel.validateData]);

  // Block submitting a part number column of line numbers (1, 2, 3...), which
  // match unrelated real parts, unless the user explicitly proceeds anyway
  const partNumberLooksLikeLineNumbers = useMemo(
    () =>
      looksLikeLineNumbers(
        excel.fullData.slice(1).map((row) => row[columnMappings.partNumber - 1])
      ),
    [excel.fullData, columnMappings.partNumber]
  );

  // A new file or a different column needs a fresh decision
  useEffect(() => {
    setForceLineNumbers(false);
  }, [excel.fullData, columnMappings.partNumber]);

  const handleFileSelect = (file: File) => {
    if (excel.validateFile(file)) {
      excel.loadFile(file);
    } else {
      excel.setError(t('errors.validationError'));
    }
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    switch (fieldId) {
      case 'worksheet':
        excel.changeWorksheet(value);
        break;
      case 'machineType':
        setMachineType(value as 'SIDEL' | 'KHS' | 'KRONES' | 'N/A');
        break;
      case 'priceList':
        setPriceList(value);
        break;
      case 'discountPercentage': {
        const parsed = parseFloat(value) || 0;
        // Clamp value between 0 and 100
        setDiscountPercentage(Math.min(100, Math.max(0, parsed)));
        break;
      }
      case 'partNumber':
        setColumnMappings((prev) => ({
          ...prev,
          partNumber: parseInt(value) || 1,
        }));
        break;
      case 'description':
        setColumnMappings((prev) => ({
          ...prev,
          description: parseInt(value) || 2,
        }));
        break;
      case 'quantity':
        setColumnMappings((prev) => ({
          ...prev,
          quantity: parseInt(value) || 3,
        }));
        break;
      case 'enableColorCoding':
        setEnableColorCoding(value === 'true');
        break;
      case 'forceLineNumbers':
        setForceLineNumbers(value === 'true');
        break;
      default:
        break;
    }
  };

  const runListParser = async () => {
    if (!excel.file || !excel.selectedWorksheet) {
      excel.setError(t('listParser.validationError'));
      return;
    }

    setIsProcessingQuote(true);
    excel.setError(null);
    setSuccessMessage(null);
    setProgressError(null);
    setProcessingStatus('submitted');
    setJobId(null);

    // Create abort controller for this polling operation
    const abortController = new AbortController();
    pollAbortControllerRef.current = abortController;

    try {
      // Step 1: Submit the job (async)
      const submitRequest: ListParserRequest = {
        file: excel.file,
        worksheetName: excel.selectedWorksheet,
        machineType,
        priceList,
        columnMappings,
        enableColorCoding,
        discountPercentage,
      };

      const submitData = await apiService.submitListParserJob(submitRequest);

      if (!submitData.jobId) {
        throw new Error('No job ID returned from server');
      }

      const currentJobId = submitData.jobId;
      setJobId(currentJobId);

      // Step 2: Poll for job completion with abort signal
      await apiService.pollJobStatus(
        currentJobId,
        (statusResponse) => {
          const status = statusResponse.status;

          // Always update progress if available
          if (statusResponse.jobProgress !== undefined) {
            setProgress(statusResponse.jobProgress);
          }

          // Update status
          if (status === 'PROCESSING') {
            setProcessingStatus('processing');
          } else if (status === 'COMPLETED') {
            setProcessingStatus('completed');
            setProgress(100);
          } else if (status === 'FAILED') {
            setProcessingStatus('failed');
          } else if (status === 'PENDING') {
            setProcessingStatus('submitted');
          }
        },
        3000,
        200,
        abortController.signal
      );

      // Step 3: Get the job result (presigned URL)
      const resultResponse = await apiService.getJobResult(currentJobId);

      const { presignedUrl, outputS3Key, jobSummary: summary } = resultResponse;
      const filename = outputS3Key?.split('/').pop() || 'processed_list.xlsx';

      // Step 4: Download the file
      await downloadFileFromUrl(presignedUrl, filename);

      // Step 5: Show success message with actual statistics
      const totalParts = summary?.total_parts ?? 0;
      const matchedParts = summary?.matched_parts ?? 0;
      setSuccessMessage(
        t('listParser.successMessage', {
          totalParts,
          matchedParts,
          machineType,
          priceList,
        })
      );
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Unknown error occurred';

      // Don't show error if user cancelled
      if (errorMsg === 'Job polling cancelled by user') {
        setProcessingStatus('idle');
        setProgress(0);
        setJobId(null);
      } else {
        console.error('Error processing list parser:', error);
        setProcessingStatus('failed');
        setProgressError(errorMsg);
        excel.setError(t('listParser.processingError', { message: errorMsg }));
      }
    } finally {
      setIsProcessingQuote(false);
    }
  };

  const handleReset = () => {
    excel.reset();
    setMachineType('SIDEL');
    setPriceList(priceLists.length > 0 ? priceLists[0].id : 'REG_USD');
    setDiscountPercentage(0);
    setEnableColorCoding(true);
    setColumnMappings({
      partNumber: 1,
      description: 2,
      quantity: 3,
    });
    setSuccessMessage(null);
    setProgress(0);
    setProgressError(null);
    setProcessingStatus('idle');
    setJobId(null);
  };

  // Generate column options based on Excel preview data
  const generateColumnOptions = () => {
    if (!excel.previewData || excel.previewData.length === 0) {
      return [];
    }

    const maxColumns = Math.max(...excel.previewData.map((row) => row.length));
    return Array.from({ length: maxColumns }, (_, index) => {
      const columnIndex = index + 1;
      const headerValue =
        excel.previewData[0] && excel.previewData[0][index]
          ? String(excel.previewData[0][index])
          : '';

      return {
        value: columnIndex.toString(),
        label: `Column ${columnIndex}${headerValue ? ` (${headerValue})` : ''}`,
      };
    });
  };

  const columnOptions = generateColumnOptions();

  // Configuration fields
  const configFields: FormField[] = [
    {
      id: 'worksheet',
      label: t('listParser.worksheetName'),
      type: 'select',
      value: excel.selectedWorksheet,
      options: excel.worksheetNames.map((name) => ({
        value: name,
        label: name,
      })),
      disabled: excel.worksheetNames.length === 0,
    },
    {
      id: 'machineType',
      label: t('listParser.machineType'),
      type: 'select',
      value: machineType,
      options: [
        { value: 'SIDEL', label: 'SIDEL' },
        { value: 'KHS', label: 'KHS' },
        { value: 'KRONES', label: 'KRONES' },
        { value: 'N/A', label: 'N/A' },
      ],
      required: true,
    },
    {
      id: 'priceList',
      label: t('listParser.priceList'),
      type: 'select',
      value: priceList,
      options: priceLists.map((pl) => ({
        value: pl.id,
        label: `${pl.description} (${pl.id})`,
      })),
      required: true,
      disabled: priceListsLoading,
    },
    {
      id: 'discountPercentage',
      label: t('listParser.discountPercentage'),
      type: 'number',
      value: discountPercentage.toString(),
      helpText: t('listParser.discountPercentageHelp'),
      min: 0,
      max: 100,
      step: 0.01,
    },
    // Column mapping fields
    {
      id: 'partNumber',
      label: t('listParser.columnMappings.partNumber'),
      type: 'select',
      value: columnMappings.partNumber.toString(),
      options: columnOptions,
      required: true,
      disabled: columnOptions.length === 0,
      error: partNumberLooksLikeLineNumbers
        ? t('listParser.columnMappings.lineNumbersError')
        : undefined,
    },
    ...(partNumberLooksLikeLineNumbers
      ? [
          {
            id: 'forceLineNumbers',
            label: t('listParser.columnMappings.forceLineNumbers'),
            type: 'checkbox' as const,
            value: forceLineNumbers ? 'true' : 'false',
            helpText: t('listParser.columnMappings.forceLineNumbersHelp'),
          },
        ]
      : []),
    {
      id: 'description',
      label: t('listParser.columnMappings.description'),
      type: 'select',
      value: columnMappings.description.toString(),
      options: columnOptions,
      required: true,
      disabled: columnOptions.length === 0,
    },
    {
      id: 'quantity',
      label: t('listParser.columnMappings.quantity'),
      type: 'select',
      value: columnMappings.quantity.toString(),
      options: columnOptions,
      required: true,
      disabled: columnOptions.length === 0,
    },
    // Color coding toggle
    {
      id: 'enableColorCoding',
      label: t('listParser.enableColorCoding'),
      type: 'checkbox',
      value: enableColorCoding ? 'true' : 'false',
      helpText: t('listParser.enableColorCodingHelp'),
    },
  ];

  // Action buttons
  const actions: ActionButton[] = [
    {
      id: 'process',
      label: t('listParser.processQuote'),
      icon: Play,
      disabled:
        !excel.file ||
        !excel.selectedWorksheet ||
        (partNumberLooksLikeLineNumbers && !forceLineNumbers),
      loading: isProcessingQuote,
    },
    {
      id: 'reset',
      label: t('listParser.reset'),
      icon: RotateCcw,
      variant: 'outline',
      disabled: isProcessingQuote,
    },
  ];

  const handleAction = (actionId: string) => {
    switch (actionId) {
      case 'process':
        runListParser();
        break;
      case 'reset':
        handleReset();
        break;
      default:
        break;
    }
  };

  // Info items
  const matchingDetails = t('listParser.infoItems.matchingDetails', {
    returnObjects: true,
  }) as string[];

  const infoItems: InfoItem[] = [
    { text: t('listParser.infoItems.upload') },
    { text: t('listParser.infoItems.mapping') },
    {
      text: t('listParser.infoItems.matching'),
      subItems: matchingDetails,
    },
    { text: t('listParser.infoItems.pricing') },
    { text: t('listParser.infoItems.download') },
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 w-full">
      <Breadcrumb currentPage={t('listParser.title')} />

      <ToolPageHeader
        title={t('listParser.title')}
        description={t('listParser.description')}
        icon={FileSpreadsheet}
      />

      <ErrorBanner error={excel.error} onDismiss={() => excel.setError(null)} />

      <SuccessBanner
        message={successMessage}
        onDismiss={() => setSuccessMessage(null)}
      />

      {/* Status indicator during processing */}
      {processingStatus === 'submitted' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
          <div className="flex-1">
            <p className="text-blue-900 font-medium">
              Job submitted - waiting for processing to start...
            </p>
            {jobId && (
              <p className="text-blue-700 text-sm mt-1">Job ID: {jobId}</p>
            )}
          </div>
        </div>
      )}

      {processingStatus === 'processing' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
          <div className="flex-1">
            <p className="text-blue-900 font-medium">
              Processing your Excel file...
            </p>
            {jobId && (
              <p className="text-blue-700 text-sm mt-1">Job ID: {jobId}</p>
            )}
          </div>
        </div>
      )}

      {showValidation && (
        <ValidationWarningBanner
          validation={excel.validation}
          onDismiss={() => setShowValidation(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <FileUploadArea
            file={excel.file}
            onFileSelect={handleFileSelect}
            onFileRemove={excel.reset}
            title={t('listParser.uploadExcelFile')}
            description={t('listParser.uploadExcelDescription')}
          />

          <DataPreviewTable
            data={excel.fullData}
            title={t('listParser.filePreview')}
            description={
              excel.fullData.length > 100
                ? t('listParser.filePreviewDescriptionLarge', {
                    count: excel.fullData.length,
                  })
                : t('listParser.filePreviewDescription')
            }
            validation={excel.validation}
            _totalRows={excel.fullData.length}
          />

          <ToolConfigurationPanel
            fields={configFields}
            onFieldChange={handleFieldChange}
          />

          <ToolActionsPanel
            actions={actions}
            onActionClick={handleAction}
            isProcessing={isProcessingQuote}
          />
        </div>

        <div>
          <ToolInfoPanel title={t('listParser.infoTitle')} items={infoItems} />
        </div>
      </div>

      {/* Job progress overlay - shows from submission through completion */}
      <JobProgressOverlay
        isVisible={
          processingStatus === 'submitted' ||
          processingStatus === 'processing' ||
          processingStatus === 'completed' ||
          processingStatus === 'failed'
        }
        progress={progress}
        jobId={jobId || undefined}
        message={t('listParser.processingMessage', {
          defaultValue: 'Processing your file...',
        })}
        errorMessage={progressError || undefined}
        isComplete={processingStatus === 'completed'}
        onCancel={() => {
          // Abort the polling if still in progress
          if (pollAbortControllerRef.current) {
            pollAbortControllerRef.current.abort();
          }
          setProcessingStatus('idle');
          setProgressError(null);
          setProgress(0);
          setJobId(null);
        }}
      />
    </div>
  );
};

export default ListParserPage;
